/**
 * Create or edit an event (Discord's "Create Event"): where (a voice
 * channel, a stage, or somewhere else, only where you may make one), the
 * channel or the place, the topic (100), the start and the end in your own
 * time (an end is needed somewhere else, optional in a channel), the
 * description (1000) and a 5:2 cover image through the crop editor and the
 * image pipeline (800 x 320). A started event keeps its start and where it
 * is, as the server does. On a phone the dialog is a sheet (§12.3).
 */
import {
  GuildScheduledEventEntityType as ET,
  GuildScheduledEventStatus as ST,
  SCHEDULED_EVENT_LIMITS as LIMITS,
  type CreateGuildScheduledEventRequest,
  type GuildScheduledEvent,
  type ModifyGuildScheduledEventRequest,
  type Snowflake,
} from '@neosocial/protocol';
import { useMemo, useRef, useState } from 'react';
import { useT } from '../i18n';
import { isApiRequestError } from '../lib/api';
import { assetUrl } from '../lib/config';
import { blobToDataUri, IMAGE_ACCEPT, imageErrorText, looksLikeImage } from '../lib/image';
import { errorText } from '../mod/actions';
import { flagOn } from '../store/config';
import { useData } from '../store/data';
import { useActiveGuildId } from '../store/selectors';
import { ImageEditor } from '../ui/ImageEditor';
import { Btn, Icon, Select, type IconName } from '../ui/kit';
import { Dialog, toast } from '../ui/overlay';
import { createTargets, type CreateTargets } from './perms';
import { eventsApi, rememberEvent, useEvents } from './store';
import {
  closeEvents,
  defaultStart,
  fromLocalParts,
  localParts,
  openEvent,
  openEvents,
  zoneName,
} from './ui';

type Kind = typeof ET.VOICE | typeof ET.STAGE_INSTANCE | typeof ET.EXTERNAL;

const KINDS: Array<{ kind: Kind; icon: IconName; label: string; sub: string }> = [
  { kind: ET.VOICE, icon: 'volume', label: 'ev.where.voice', sub: 'ev.editor.voiceSub' },
  { kind: ET.STAGE_INSTANCE, icon: 'stage', label: 'ev.where.stage', sub: 'ev.editor.stageSub' },
  { kind: ET.EXTERNAL, icon: 'globe', label: 'ev.where.external', sub: 'ev.editor.externalSub' },
];

function allowedKinds(t: CreateTargets): Kind[] {
  const out: Kind[] = [];
  if (t.voice.length) out.push(ET.VOICE);
  if (t.stage.length) out.push(ET.STAGE_INSTANCE);
  if (t.external) out.push(ET.EXTERNAL);
  return out;
}

/** The first field error of a 400 (the server's words), or the usual error text. */
function serverError(e: unknown): string {
  if (isApiRequestError(e) && e.body?.errors) {
    const first = Object.values(e.body.errors as Record<string, Array<{ message: string }>>)[0];
    if (first?.[0]?.message) return first[0].message;
  }
  return errorText(e);
}

export function EventEditor({
  id,
  channelId,
  back,
}: {
  id: Snowflake | null;
  channelId?: Snowflake;
  back: 'list' | 'event' | null;
}) {
  const { t } = useT();
  const gid = useActiveGuildId();
  const editing: GuildScheduledEvent | null = useEvents((s) => (id ? (s.byId[id] ?? null) : null));
  const d = useData((s) => s);
  const targets = useMemo(() => createTargets(d, gid), [d, gid]);
  const live = editing?.status === ST.ACTIVE;
  const kinds = allowedKinds(targets);
  const initialKind: Kind =
    (editing?.entity_type as Kind | undefined) ??
    (channelId && d.channels[channelId]?.type === 13 ? ET.STAGE_INSTANCE : (kinds[0] ?? ET.VOICE));
  const [kind, setKind] = useState<Kind>(initialKind);
  const channelsFor = (k: Kind) =>
    k === ET.VOICE ? targets.voice : k === ET.STAGE_INSTANCE ? targets.stage : [];
  const [channel, setChannel] = useState<Snowflake>(
    editing?.channel_id ?? channelId ?? channelsFor(initialKind)[0]?.id ?? '',
  );
  const [location, setLocation] = useState(editing?.entity_metadata?.location ?? '');
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const start0 = editing ? new Date(editing.scheduled_start_time) : defaultStart();
  const [startDate, setStartDate] = useState(localParts(start0).date);
  const [startTime, setStartTime] = useState(localParts(start0).time);
  const end0 = editing?.scheduled_end_time
    ? new Date(editing.scheduled_end_time)
    : new Date(start0.getTime() + 2 * 3_600_000);
  const [hasEnd, setHasEnd] = useState(
    !!editing?.scheduled_end_time || initialKind === ET.EXTERNAL,
  );
  const [endDate, setEndDate] = useState(localParts(end0).date);
  const [endTime, setEndTime] = useState(localParts(end0).time);
  // the cover: undefined unchanged, null removed, a data URI new
  const [cover, setCover] = useState<string | null | undefined>(undefined);
  const [cropping, setCropping] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const file = useRef<HTMLInputElement>(null);

  const needEnd = kind === ET.EXTERNAL;
  const withEnd = hasEnd || needEnd;
  const shownCover = cover === undefined ? assetUrl(editing?.image ?? null) : cover;
  // a started event's place stays (the server refuses a move)
  const whereLocked = live;
  const kindChoices = whereLocked ? [kind] : editing ? KINDS.map((k) => k.kind) : kinds;

  const pickKind = (k: Kind) => {
    setKind(k);
    const list = channelsFor(k);
    if (k !== ET.EXTERNAL && !list.some((c) => c.id === channel)) setChannel(list[0]?.id ?? '');
    if (k === ET.EXTERNAL) setHasEnd(true);
  };

  const check = (): string | null => {
    if (!name.trim()) return t('ev.editor.err.name');
    if (kind !== ET.EXTERNAL && !channel) return t('ev.editor.err.channel');
    if (kind === ET.EXTERNAL && !location.trim()) return t('ev.editor.err.location');
    const s = fromLocalParts(startDate, startTime);
    if (!s) return t('ev.editor.err.when');
    const startMoved = !editing || s.getTime() !== Date.parse(editing.scheduled_start_time);
    if (startMoved && !live && s.getTime() <= Date.now()) return t('ev.editor.err.past');
    if (withEnd) {
      const e = fromLocalParts(endDate, endTime);
      if (!e) return t('ev.editor.err.needEnd');
      if (e.getTime() <= s.getTime()) return t('ev.editor.err.end');
    }
    return null;
  };

  const submit = async () => {
    const problem = check();
    if (problem) {
      setErr(problem);
      return;
    }
    if (!gid) return;
    setErr(null);
    setBusy(true);
    const s = fromLocalParts(startDate, startTime)!;
    const e = withEnd ? fromLocalParts(endDate, endTime) : null;
    const body: CreateGuildScheduledEventRequest = {
      name: name.trim(),
      description: description.trim() || null,
      entity_type: kind,
      channel_id: kind === ET.EXTERNAL ? null : channel,
      entity_metadata: kind === ET.EXTERNAL ? { location: location.trim() } : null,
      scheduled_start_time: s.toISOString(),
      scheduled_end_time: e ? e.toISOString() : null,
      privacy_level: 2,
    };
    if (cover !== undefined) body.image = cover;
    try {
      let out: GuildScheduledEvent;
      if (editing) {
        const patch: ModifyGuildScheduledEventRequest = { ...body };
        // a started event keeps its start and its place: send only what may change
        if (live) {
          delete patch.scheduled_start_time;
          delete patch.entity_type;
          delete patch.channel_id;
        }
        out = await eventsApi.modify(editing.guild_id, editing.id, patch);
      } else out = await eventsApi.create(gid, body);
      rememberEvent(out);
      toast(t(editing ? 'ev.saved' : 'ev.created'), { icon: 'calendar', tone: 'ok' });
      openEvent(out.id);
    } catch (x) {
      setErr(serverError(x));
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    if (back === 'event' && editing) openEvent(editing.id);
    else if (back === 'list') openEvents();
    else closeEvents();
  };

  const left = (max: number, v: string) => max - [...v].length;

  return (
    <Dialog
      title={t(editing ? 'ev.editor.edit' : 'ev.editor.new')}
      icon="calendar"
      tone="mode"
      size="md"
      cls="ev-dlg ev-editor"
      onClose={cancel}
      actions={
        <>
          <Btn kind="ghost" size="sm" onClick={cancel}>
            {t('c.cancel')}
          </Btn>
          <Btn kind="primary" size="sm" busy={busy} onClick={() => void submit()}>
            {t(editing ? 'ev.editor.save' : 'ev.editor.create')}
          </Btn>
        </>
      }
    >
      <fieldset className="ns-field ev-kinds">
        <legend className="ns-label">{t('ev.editor.where')}</legend>
        <div className="ev-kind-row" role="radiogroup" aria-label={t('ev.editor.where')}>
          {KINDS.filter((k) => kindChoices.includes(k.kind)).map((k) => {
            const possible =
              kinds.includes(k.kind) || (!!editing && k.kind === editing.entity_type);
            return (
              <button
                key={k.kind}
                type="button"
                role="radio"
                className="ev-kind"
                aria-checked={kind === k.kind}
                disabled={!possible || whereLocked}
                title={!possible ? t('ev.editor.noRoom') : undefined}
                onClick={() => pickKind(k.kind)}
              >
                <Icon name={k.icon} />
                <span className="ev-kind-t">{t(k.label)}</span>
                <span className="ev-kind-s">{t(k.sub)}</span>
              </button>
            );
          })}
        </div>
      </fieldset>
      {kind === ET.EXTERNAL ? (
        <label className="ns-field">
          <span className="ns-label">{t('ev.editor.location')}</span>
          <input
            className="nx-input"
            dir="auto"
            value={location}
            maxLength={LIMITS.LOCATION_MAX}
            placeholder={t('ev.editor.locationPh')}
            onChange={(e) => setLocation(e.target.value)}
          />
        </label>
      ) : (
        <label className="ns-field">
          <span className="ns-label">{t('ev.editor.channel')}</span>
          <Select
            value={channel}
            disabled={whereLocked}
            onChange={(e) => setChannel(e.target.value)}
          >
            {/* the event's own channel stays listed while editing */}
            {[
              ...channelsFor(kind),
              ...(editing?.channel_id &&
              !channelsFor(kind).some((c) => c.id === editing.channel_id) &&
              d.channels[editing.channel_id]
                ? [d.channels[editing.channel_id]!]
                : []),
            ].map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </label>
      )}
      <label className="ns-field">
        <span className="ns-label">{t('ev.editor.name')}</span>
        <input
          className="nx-input"
          dir="auto"
          value={name}
          maxLength={LIMITS.NAME_MAX}
          placeholder={t('ev.editor.namePh')}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <div className="ev-when-row">
        <label className="ns-field">
          <span className="ns-label">{t('ev.editor.startDate')}</span>
          <input
            className="nx-input"
            type="date"
            value={startDate}
            disabled={live}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </label>
        <label className="ns-field">
          <span className="ns-label">{t('ev.editor.startTime')}</span>
          <input
            className="nx-input"
            type="time"
            value={startTime}
            disabled={live}
            onChange={(e) => setStartTime(e.target.value)}
          />
        </label>
      </div>
      {withEnd ? (
        <div className="ev-when-row">
          <label className="ns-field">
            <span className="ns-label">{t('ev.editor.endDate')}</span>
            <input
              className="nx-input"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>
          <label className="ns-field">
            <span className="ns-label">{t('ev.editor.endTime')}</span>
            <input
              className="nx-input"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </label>
        </div>
      ) : null}
      {needEnd ? null : (
        <div className="ev-end-toggle">
          <Btn
            size="xs"
            kind="quiet"
            icon={withEnd ? 'x' : 'clock'}
            onClick={() => setHasEnd(!withEnd)}
          >
            {t(withEnd ? 'ev.editor.removeEnd' : 'ev.editor.addEnd')}
          </Btn>
          {!withEnd ? <span className="ns-muted">{t('ev.editor.endOptional')}</span> : null}
        </div>
      )}
      <p className="ns-muted ev-tz">{t('ev.tz', { tz: zoneName() })}</p>
      <label className="ns-field">
        <span className="ns-label">{t('ev.editor.description')}</span>
        <textarea
          className="nx-textarea"
          dir="auto"
          rows={3}
          value={description}
          maxLength={LIMITS.DESCRIPTION_MAX}
          placeholder={t('ev.editor.descriptionPh')}
          onChange={(e) => setDescription(e.target.value)}
        />
        <span className="ns-help">
          {t('ev.editor.left', { n: left(LIMITS.DESCRIPTION_MAX, description) })}
        </span>
      </label>
      <div className="ns-field ev-cover-field">
        <span className="ns-label">{t('ev.editor.cover')}</span>
        {shownCover ? (
          <img className="ev-cover ev-cover-preview" src={shownCover} alt="" />
        ) : (
          <p className="ns-muted">{t('ev.editor.coverHint')}</p>
        )}
        <div className="ns-row-btns">
          <Btn
            size="sm"
            icon="image"
            onClick={() =>
              flagOn('image_uploads')
                ? file.current?.click()
                : toast(t('cfg.off.image_uploads'), { icon: 'image', tone: 'warn' })
            }
          >
            {t(shownCover ? 'ev.editor.coverChange' : 'ev.editor.coverAdd')}
          </Btn>
          {shownCover ? (
            <Btn size="sm" kind="quiet" onClick={() => setCover(editing?.image ? null : undefined)}>
              {t('ev.editor.coverRemove')}
            </Btn>
          ) : null}
        </div>
        <input
          ref={file}
          type="file"
          accept={IMAGE_ACCEPT}
          hidden
          data-event-cover=""
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (!f) return;
            if (!looksLikeImage(f)) {
              toast(t('img.err.unsupported'), { tone: 'warn', icon: 'image' });
              return;
            }
            setCropping(f);
          }}
        />
      </div>
      {err ? (
        <p className="ns-err" role="alert">
          {err}
        </p>
      ) : null}
      {cropping ? (
        <ImageEditor
          file={cropping}
          purpose="event_cover"
          onCancel={() => setCropping(null)}
          onDone={(img) => {
            setCropping(null);
            blobToDataUri(img.blob).then(setCover, (x: unknown) =>
              toast(imageErrorText(t, x), { tone: 'warn', icon: 'image' }),
            );
          }}
        />
      ) : null}
    </Dialog>
  );
}
